"use client";

import { c as _c } from "react/compiler-runtime";
import type { ApiWave } from "@/generated/models/ApiWave";
import { getRandomColorWithSeed, getTimeAgoShort } from "@/helpers/Helpers";
import ContentDisplay from "@/components/waves/drops/ContentDisplay";
import { buildProcessedContent, type ProcessedContent } from "@/components/waves/drops/media-utils";
import { getScaledImageUri, ImageScale } from "@/helpers/image.helpers";
import { getWaveRoute } from "@/helpers/navigation.helpers";
import Image from "next/image";
import Link from "next/link";
interface ExploreWaveCardProps {
  readonly wave: ApiWave;
}
export function ExploreWaveCard(t0) {
  const $ = _c(42);
  const {
    wave
  } = t0;
  const t1 = wave.chat.scope.group?.is_direct_message ?? false;
  let t2;
  if ($[0] !== t1 || $[1] !== wave.id) {
    t2 = getWaveRoute({
      waveId: wave.id,
      isDirectMessage: t1,
      isApp: false
    });
    $[0] = t1;
    $[1] = wave.id;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const waveHref = t2;
  const author = wave.author;
  let t3;
  if ($[3] !== author.banner1_color || $[4] !== author.handle) {
    t3 = author.banner1_color ?? getRandomColorWithSeed(author.handle ?? "");
    $[3] = author.banner1_color;
    $[4] = author.handle;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const banner1 = t3;
  let t4;
  if ($[6] !== author.banner2_color || $[7] !== author.handle) {
    t4 = author.banner2_color ?? getRandomColorWithSeed(author.handle ?? "");
    $[6] = author.banner2_color;
    $[7] = author.handle;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const banner2 = t4;
  let t5;
  if ($[9] !== banner1 || $[10] !== banner2 || $[11] !== wave.picture) {
    t5 = !wave.picture ? {
      background: `linear-gradient(135deg, ${banner1} 0%, ${banner2} 100%)`
    } : undefined;
    $[9] = banner1;
    $[10] = banner2;
    $[11] = wave.picture;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const imageAreaStyle = t5;
  const lastMessageTime = wave.last_drop_time;
  const hasDrops = wave.metrics.drops_count > 0;
  let t6;
  if ($[13] !== wave) {
    t6 = getWavePreviewContent(wave);
    $[13] = wave;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const descriptionPreview = t6;
  const t7 = `View wave ${wave.name}`;
  let t8;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="tw-pointer-events-none tw-absolute tw-inset-0 tw-z-10 tw-rounded-xl tw-border tw-border-solid tw-border-white/10" />;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== wave.name || $[17] !== wave.picture) {
    t9 = wave.picture && <Image src={getScaledImageUri(wave.picture, ImageScale.AUTOx450)} alt={`${wave.name} cover`} fill={true} sizes="(max-width: 639px) 100vw, (max-width: 1023px) 50vw, 33vw" className="tw-object-cover tw-transition-transform tw-duration-700 tw-will-change-transform desktop-hover:group-hover:tw-scale-105" />;
    $[16] = wave.name;
    $[17] = wave.picture;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== imageAreaStyle || $[20] !== t9) {
    t10 = <div className="tw-relative tw-aspect-[20/9] tw-overflow-hidden" style={imageAreaStyle}>{t9}</div>;
    $[19] = imageAreaStyle;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== wave.name) {
    t11 = <span className="tw-m-0 tw-line-clamp-1 tw-break-words tw-text-sm tw-font-semibold tw-leading-tight tw-text-white tw-transition-colors group-hover:tw-text-white/80 sm:tw-text-base">{wave.name}</span>;
    $[22] = wave.name;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== descriptionPreview) {
    t12 = descriptionPreview && <MessagePreviewContent previewContent={descriptionPreview} />;
    $[24] = descriptionPreview;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== hasDrops || $[27] !== lastMessageTime || $[28] !== wave.metrics.drops_count) {
    t13 = hasDrops && <div className="tw-mt-6 tw-flex tw-flex-wrap tw-items-center tw-gap-x-1.5 tw-gap-y-1 tw-text-[11px] tw-text-iron-500 sm:tw-text-xs"><span className="tw-relative tw-flex tw-h-2 tw-w-2"><span className="tw-absolute tw-inline-flex tw-h-full tw-w-full tw-animate-ping tw-rounded-full tw-bg-success/60" /><span className="tw-relative tw-inline-flex tw-h-2 tw-w-2 tw-rounded-full tw-bg-success" /></span><span className="tw-text-iron-300">{getTimeAgoShort(lastMessageTime)} ·{" "}{wave.metrics.drops_count.toLocaleString()}</span>{" "}drops</div>;
    $[26] = hasDrops;
    $[27] = lastMessageTime;
    $[28] = wave.metrics.drops_count;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== hasDrops) {
    t14 = !hasDrops && <div className="tw-mt-3 tw-text-[11px] tw-text-iron-500 sm:tw-text-xs">No drops yet</div>;
    $[30] = hasDrops;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== t11 || $[33] !== t12 || $[34] !== t13 || $[35] !== t14) {
    t15 = <div className="tw-px-4 tw-py-6 sm:tw-p-5">{t11}{t12}{t13}{t14}</div>;
    $[32] = t11;
    $[33] = t12;
    $[34] = t13;
    $[35] = t14;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== t10 || $[38] !== t15 || $[39] !== t7 || $[40] !== waveHref) {
    t16 = <Link href={waveHref} prefetch={false} className="tw-group tw-relative tw-block tw-overflow-hidden tw-rounded-xl tw-bg-iron-950 tw-text-left tw-no-underline tw-transition-all tw-duration-300 tw-ease-out focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-iron-500/30" aria-label={t7}>{t8}{t10}{t15}</Link>;
    $[37] = t10;
    $[38] = t15;
    $[39] = t7;
    $[40] = waveHref;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  return t16;
}
function MessagePreviewContent(t0) {
  const $ = _c(2);
  const {
    previewContent
  } = t0;
  if (!previewContent) {
    return null;
  }
  let t1;
  if ($[0] !== previewContent) {
    t1 = <ContentDisplay content={previewContent} shouldClamp={false} className="tw-mt-1 tw-flex tw-min-w-0 tw-flex-1 tw-items-start tw-gap-1 tw-overflow-hidden tw-text-iron-500" textClassName="tw-line-clamp-2 tw-break-words tw-text-[10px] tw-font-normal tw-leading-tight sm:tw-text-xs" linkify={false} />;
    $[0] = previewContent;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function getWavePreviewContent(wave: ApiWave): ProcessedContent | null {
  const descriptionParts = wave.description_drop.parts;
  const combinedText = descriptionParts.map(part => part.content?.trim()).filter((content): content is string => Boolean(content)).join("\n\n");
  const media = descriptionParts.flatMap(part => part.media);
  if (!combinedText && media.length === 0) {
    return null;
  }
  return buildProcessedContent(combinedText || null, media);
}